import {
  MARKER_OPACITY_AT_HIGH_ZOOM,
  MARKER_OPACITY_AT_LOW_ZOOM,
} from "../BatchedCircles/constants";
import type { DetectedObject } from "../../types";
import type { BatchedRectPath } from "./types";

interface BuildBatchedRectPathsArgs {
  objects: DetectedObject[];
  isLowZoom: boolean;
  excludeId?: string | null;
  /**
   * Expand each rect outward by this amount in PDF coordinate space on every
   * side. Used at low zoom to keep tiny rects visible without resorting to a
   * non-scaling stroke (which would paint a halo outside the rect). Compute
   * as `desiredScreenPx / zoomLevel` to stay constant in screen space.
   */
  inflatePdf?: number;
}

const toColorKey = (rgb: [number, number, number], opacity: number): string =>
  `${rgb[0]},${rgb[1]},${rgb[2]},${opacity}`;

const rectSubpath = (x1: number, y1: number, x2: number, y2: number): string =>
  `M${x1},${y1}H${x2}V${y2}H${x1}Z`;

export const buildBatchedRectPaths = ({
  objects,
  isLowZoom,
  excludeId,
  inflatePdf = 0,
}: BuildBatchedRectPathsArgs): BatchedRectPath[] => {
  const groups = new Map<
    string,
    { color: string; opacity: number; subpaths: string[] }
  >();

  for (const object of objects) {
    if (object.id === excludeId) continue;
    if (!object.bbox) continue;

    const opacity =
      isLowZoom && object.status !== "uninstalled"
        ? MARKER_OPACITY_AT_LOW_ZOOM
        : MARKER_OPACITY_AT_HIGH_ZOOM;
    const colorKey = toColorKey(object.rgb, opacity);

    let group = groups.get(colorKey);
    if (!group) {
      group = {
        color: `rgb(${object.rgb[0]},${object.rgb[1]},${object.rgb[2]})`,
        opacity,
        subpaths: [],
      };
      groups.set(colorKey, group);
    }

    const { x1, y1, x2, y2 } = object.bbox;
    group.subpaths.push(
      rectSubpath(
        x1 - inflatePdf,
        y1 - inflatePdf,
        x2 + inflatePdf,
        y2 + inflatePdf,
      ),
    );
  }

  const result: BatchedRectPath[] = [];
  for (const group of groups.values()) {
    result.push({
      color: group.color,
      pathData: group.subpaths.join(""),
      opacity: group.opacity,
    });
  }

  return result;
};
